<template>
  <div class="tw:text-gray-800 od" data-onward>
    <form @submit.prevent="search">

      <div class="tw:inline-block">
        <div class="od-toggle-container">
          <div 
            class="od-toggle-item"
            :class="sector.is('oneway') && 'active'"
            @click="sector.set('oneway')"
          >
            One Way <span v-if="pricing.show">${{ pricing.oneway }}</span>
          </div>
          <div 
            class="od-toggle-item"
            :class="sector.is('roundtrip') && 'active'"
            @click="sector.set('roundtrip')"
          >
            Round Trip <span v-if="pricing.show">${{ pricing.roundtrip }}</span>
          </div>
        </div>
      </div>

      <div class="od-row-one">
        <fieldset class="tw:w-full">
          <legend>Departure Airport</legend>
          <AutoCompleteAirport v-model="form.from" required/>
        </fieldset>

        <fieldset class="tw:w-full" >
          <legend>Destination Airport</legend>
          <AutoCompleteAirport v-model="form.to"required/>
        </fieldset>

        <fieldset class="tw:w-full">
          <legend>Passengers</legend>
          <select class="od-input tw:w-full" v-model="form.pax" required>
            <option value="">Select One</option>
            <option v-for="{ label, value } of passengers" :value="value">{{ label }}</option>
          </select>
        </fieldset>
      </div>

      <div class="od-row-two">
        <fieldset>
          <legend>Departure Date</legend>
          <input
            class="od-input tw:w-full"
            v-model="form.departure"
            type="date"
            :min="dayjs().add(1, 'd').format('YYYY-MM-DD')"
            required
          />
          <div ref="anchor"></div>
        </fieldset>

        <fieldset v-if="sector.is('roundtrip')">
          <legend>Return Date</legend>
          <input
            class="od-input tw:w-full"
            v-model="form.return"
            type="date"
            :min="form.departure"
            required
          />
        </fieldset>

        <div v-if="features.extendedPlan" class="tw:!text-base tw:!text-gray-600 tw:!mt-2 tw:!opacity-80 tw:!block tw:md:!hidden">
          <span class="tw:!text-sm">Ticket valid:</span>
          <div class="tw:!my-2 tw:!flex tw:!gap-3">
            <div class="tw:!flex tw:!items-center tw:!text-sm">
              <input type="radio" id="plan-48h-mobile" name="duration-plan" :value="undefined" v-model="form.duration" class="tw:mr-2 tw:!cursor-pointer">
              <label for="plan-48h-mobile" class="tw:!cursor-pointer">48-Hour Plan</label>
            </div>
            <div class="tw:!flex tw:!items-center tw:!text-sm">
              <input type="radio" id="plan-7d-mobile" name="duration-plan" value="7days" v-model="form.duration" class="tw:mr-2 tw:!cursor-pointer">
              <label for="plan-7d-mobile" class="tw:!cursor-pointer">7-Day Plan</label>
            </div>
          </div>
          <a @click="expandPlan = !expandPlan" class="tw:!text-sm tw:!cursor-pointer tw:!underline tw:!text-gray-600">Which plan should you choose for flight reservation?</a>
          <div v-if="expandPlan" class="tw:mt-2 tw:text-sm">
            While both options provide authentic flight reservations with real 6-digit PNR codes checkable on airline websites, their validity periods differ to match your exact needs:
            <ul class="tw:list-disc tw:pl-5 tw:mt-2">
              <li><strong>For Airport/Immigration Use:</strong> Choose the 48-Hour Plan. Border agents only check the real-time status of your ticket at the moment you pass through. Once you safely land, the ticket can lapse.</li>
              <li><strong>For Visa Applications:</strong> Choose the 7-Day Plan. Embassies require a valid flight itinerary throughout their entire screening process. If they check an expired code, your application may be flagged. Investing in our extended plan guarantees your ticket remains active whenever the embassy reviews your file.</li>
            </ul>
          </div>
        </div>

        <p v-else class="tw:!text-base tw:!text-gray-600 tw:!mt-2 tw:!opacity-80 tw:!block tw:md:!hidden">
          If you book at least 2 months in advance, the reservation can be the longest
        </p>

        <div class="tw:!content-end tw:!mt-5 tw:md:!mt-0">
          <button class="od-button" :disabled="timer.clock">Search Flight</button>
        </div>
      </div>

      <Transition name="fade" mode="out-in">
        <div v-if="timer.clock" class="tw:!px-2 tw:!mt-2 tw:!text-sm">
          <p class="tw:opacity-80">Finding any flight in <NumberFlow :value="timer.value" />s ...</p>
        </div>

        <div v-else-if="features.extendedPlan" class="tw:!text-base tw:!text-gray-600 tw:!mt-2 tw:!opacity-80 tw:!hidden tw:md:!block">
          <span class="tw:!text-sm">Ticket valid:</span>
          <div class="tw:mb-2 tw:!flex tw:!gap-3">
            <div class="tw:!flex tw:!items-center tw:!text-sm">
              <input type="radio" id="plan-48h-desktop" :value="undefined" v-model="form.duration" class="tw:mr-2 tw:!cursor-pointer">
              <label for="plan-48h-desktop" class="tw:!cursor-pointer">48-Hour Plan</label>
            </div>
            <div class="tw:!flex tw:!items-center tw:!text-sm">
              <input type="radio" id="plan-7d-desktop" value="7days" v-model="form.duration" class="tw:mr-2 tw:!cursor-pointer">
              <label for="plan-7d-desktop" class="tw:!cursor-pointer">7-Day Plan</label>
            </div>
          </div>
          <a @click="expandPlan = !expandPlan" class="tw:!text-sm tw:!cursor-pointer tw:!underline tw:!text-gray-600">Which plan should you choose for flight reservation?</a>
          <div v-if="expandPlan" class="tw:!mt-2 tw:!text-sm">
            While both options provide authentic flight reservations with real 6-digit PNR codes checkable on airline websites, their validity periods differ to match your exact needs:
            <ul class="tw:!list-disc tw:!pl-5 tw:!mt-2">
              <li><strong>For Airport/Immigration Use:</strong> Choose the 48-Hour Plan. Border agents only check the real-time status of your ticket at the moment you pass through. Once you safely land, the ticket can lapse.</li>
              <li><strong>For Visa Applications:</strong> Choose the 7-Day Plan. Embassies require a valid flight itinerary throughout their entire screening process. If they check an expired code, your application may be flagged. Investing in our extended plan guarantees your ticket remains active whenever the embassy reviews your file.</li>
            </ul>
          </div>
        </div>

        <p v-else class="tw:!text-base tw:!text-gray-600 tw:!mt-2 tw:!opacity-80 tw:!hidden tw:md:!block">
          If you book at least 2 months in advance, the reservation can be the longest
        </p>
      </Transition>
    </form>

    <BookingSectionPartial ref="booker" :anchor="anchor" />
  </div>
</template>

<script lang="ts" setup>
import AutoCompleteAirport, { type Airport } from '~/components/feat/AutoCompleteAirport.vue'
import dayjs from 'dayjs'
import { useSessionStorage } from '@vueuse/core'
import NumberFlow from '@number-flow/vue'
import { reactive, ref } from 'vue'
import { useRotateKeyTransition } from './composables/useTransition'
import type { IFlight } from './types'
import { axios } from './composables/general'
import BookingSectionPartial from './components/BookingSectionPartial.vue'
import { usePricingMode } from './composables/usePricingMode'
import { useSiteFeatures } from './composables/useSiteFeatures'

const pricing = usePricingMode()
const features = useSiteFeatures()
const anchor = ref<HTMLElement>()
const loading = ref(false)
const expandPlan = ref(false)
const booker = ref<InstanceType<typeof BookingSectionPartial>>()
const sector = reactive(useRotateKeyTransition(['oneway', 'roundtrip']))

const passengers = [ ... new Array(9).keys()].map((_, i) => ({ value: i + 1, label: `${i + 1} passenger${i ? 's' : ''}` }))

const form = useSessionStorage('search:session', {
  from: undefined as Airport|undefined,
  to: undefined as Airport|undefined,
  departure: dayjs().add(2, 'months').format('YYYY-MM-DD'),
  return: dayjs().add(2, 'months').add(1, 'd').format('YYYY-MM-DD'),
  pax: '' as number|string,
  duration: undefined as '7days' | undefined
})

const timer = reactive({
  clock: undefined as any,
  value: 30,
  start() {
    this.clock && clearInterval(this.clock)
    this.value = 30
    this.clock = setInterval(() => {
      this.value = this.value > 1 ? this.value - 1 : 30
    }, 1000)
  },
  stop() {
    this.clock && clearInterval(this.clock)
    this.value = 30
    this.clock = undefined
  }
})

const search = () => {
  if (timer.clock) {
    return
  }

  if (features.redirectUrl) {
    window.location.href = features.redirectUrl
    return
  }

  const timeout = setTimeout(() => {
    timer.start()
    loading.value = true
  }, 200)

  const [from, to] = [form.value.from?.iata!, form.value.to?.iata!]

  return axios.post<{ id: string, itineraries: IFlight[][] }>('/public/v2/flight/search', {
    routes: [
      { from, to, date: form.value.departure },
      sector.is('roundtrip') && { to: from, from: to, date: form.value.return}
    ].filter(Boolean),
    passenger: { adult: +form.value.pax },
    duration: form.value.duration
  }, {
    headers: {
      // @ts-ignore require tracker.js from air.onwardfree.com
      'X-affiliate-tracker': window._onwardAffiliateTracker?.id
    }
  })
    .then(({ data }) => {
      booker.value?.display({
        pax: +form.value.pax,
        depart: data.itineraries[0] ?? [],
        returning: data.itineraries[1],
        mode: sector.current,
        search: data.id,
        duration: form.value.duration
      })
    })
    .catch(() => {
      booker.value?.display({
        pax: +form.value.pax,
        depart: [],
        mode: sector.current,
        search: '',
        duration: form.value.duration
      })
    })
    .finally(() => {
      clearTimeout(timeout)
      timer.stop()
    })
}
</script>

<style lang="css" scoped>
@reference '~/style.css';

.od-toggle-container {
  @apply tw:flex tw:p-1 tw:!space-x-1 tw:bg-stone-100 tw:rounded-lg tw:[&>div]:cursor-pointer tw:[&>div]:transition;
}

.od-toggle-item {
  @apply tw:p-2 tw:hover:!bg-yellow-200 tw:active:!bg-yellow-300 tw:rounded-lg;
}

.od-toggle-item.active {
  @apply tw:bg-yellow-400;
}

.od-row-one {
  @apply tw:flex tw:flex-col tw:md:flex-row tw:!space-x-3;
}

.od-row-two {
  @apply tw:flex tw:flex-col tw:md:flex-row tw:md:!space-x-3 tw:md:mt-2;
}

.od-dark .od-toggle-item {
  @apply tw:hover:!bg-gray-900 tw:active:!bg-gray-800 tw:hover:!text-gray-200;
}

.od-dark .od-toggle-item.active {
  @apply tw:text-gray-100 tw:bg-gray-900;
}
</style>